/* Re-consent gate — when TOS_VERSION or PRIVACY_VERSION bumps server-side, every
   existing account must review and re-accept the current documents before continuing.
   Blocks the whole site (not CEF overlays) until accepted. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScrollText } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ConsentGate() {
  const [needed, setNeeded] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (window.location.pathname.startsWith("/cef/")) return;
    if (!localStorage.getItem("nc_token")) return;
    api.get("/security/consent")
      .then((r) => { if (r.data.required) setNeeded(r.data); })
      .catch(() => {});
  }, []);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/security/consent", { tos_version: needed.current_tos, privacy_version: needed.current_privacy });
      toast.success("Agreement recorded — welcome back");
      setNeeded(null);
    } catch {
      toast.error("Could not record your agreement — try again");
    } finally {
      setBusy(false);
    }
  };

  if (!needed) return null;

  return (
    <div data-testid="consent-gate" className="fixed inset-0 z-[95] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4">
      <div className="w-full max-w-md border border-white/15 bg-[#0a0a0a] p-6 flex flex-col gap-4">
        <div className="flex items-center gap-2 text-nc-blue">
          <ScrollText className="w-5 h-5" />
          <h2 className="font-sans font-bold uppercase tracking-widest text-sm">Updated legal documents</h2>
        </div>
        <p className="font-mono text-xs text-zinc-400 leading-relaxed">
          Our <Link to="/eula" data-testid="consent-tos-link" className="text-nc-blue underline underline-offset-2">Terms of Service</Link> (v{needed.current_tos}) and{" "}
          <Link to="/privacy" data-testid="consent-privacy-link" className="text-nc-blue underline underline-offset-2">Privacy Policy</Link> (v{needed.current_privacy}) have been updated.
          Please review and accept the current versions to continue. This acceptance is recorded with a timestamp against your account.
        </p>
        <button data-testid="consent-accept" onClick={accept} disabled={busy}
          className="w-full bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-50 text-white font-sans font-semibold uppercase tracking-[0.2em] text-sm py-3 transition-colors">
          {busy ? "Recording…" : "I have reviewed and accept"}
        </button>
      </div>
    </div>
  );
}
